import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { FieldWrapper } from './FieldWrapper';
import { SelectIcon } from '@td-client-frontend/icons';
import clsx from 'clsx';
import { TextInput } from './TextField';
import { useClickOutside, useEventListener } from '../../hooks';
import { Spinner } from '../Spinner'

interface SectionsSelectOption {
  label: string;
  items: { value: string; label: string }[];
}

interface PropsSectionsSelect {
  name: string;
  options: SectionsSelectOption[];
  value: string | null;
  label: string;
  placeholder: string;
  onChange: (str: string) => void;
  error: string;
  isLoading: boolean,
  isError: boolean;
  required: boolean;
  maxVisible: number;
  onSearchTextChange: (str: string) => void;
}

const scrollConfig: ScrollIntoViewOptions = {
  block: 'nearest',
  behavior: 'smooth'
};
const maxVisibleOptionQty = 4;

export function SectionsSelect({
  name,
  options,
  value,
  label,
  placeholder,
  onChange,
  error,
  isLoading,
  isError,
  required,
  maxVisible = maxVisibleOptionQty,
  onSearchTextChange
}: PropsSectionsSelect) {
  /**
   * Internal State
   */
  const [isOpen, setIsOpen] = useState(false);
  const [shouldFilter, setShouldFilter] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [hovered, setHovered] = useState(-1);
  /**
   * Refs
   */
  const listRef = useRef<HTMLUListElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const itemHeight = _.get(inputRef, 'current?.offsetHeight', 48);
  /**
   * Filter items by search term
   */
  const filtered = useMemo(() => {
    if (!searchText || !shouldFilter) {
      return options;
    }
    return options.filter(({ items }) =>
      items.filter(({ label }) => label.toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [options, searchText, shouldFilter]);

  useEffect(()=>{
    if(isOpen){
      dropdownRef.current?.scrollIntoView(scrollConfig);
    }
  });

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    if (!value) {
      // if selected value is messed in a search box, reset text field on closing drop down
      setSearchText('');
    }
  }, [value]);

  const selectItem = useCallback(
    ({ value, label }) => {
      onChange(value);
      setIsOpen(false);
      setSearchText(label);
      setShouldFilter(false);
      setHovered(-1);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (event) => {
      const { keyCode } = event;
      let next;
      switch (keyCode) {
        case 40: // down
          next = hovered + 1;
          if (next < filtered.length) {
            setHovered(next);
            listRef.current!.children[next].scrollIntoView(scrollConfig);
            if (!isOpen) {
              setIsOpen(true);
            }
          }
          break;
        case 38: // up
          next = hovered - 1;
          if (next >= 0) {
            listRef.current!.children[next].scrollIntoView(scrollConfig);
            setHovered(next);
          }
          break;
        case 27: // esc
          if (isOpen) {
            event.stopPropagation();
          }
          setIsOpen(false);
          break;
        case 13: // enter
          event.preventDefault(); // don't submit the form by accident
          if (filtered[hovered]) {
            selectItem(filtered[hovered]);
          }
          break;
        case 9: // tab
          setIsOpen(false);
          setHovered(-1);
          if (!value) {
            setSearchText('');
          }
          break;
        case 35: // end
          event.preventDefault();
          next = filtered.length - 1;
          setHovered(next);
          listRef.current!.children[next].scrollIntoView(scrollConfig);
          break;
        case 36: // home
          event.preventDefault();
          setHovered(0);
          listRef.current!.children[0].scrollIntoView(scrollConfig);
          break;
        default:
      }
    },
    [hovered, filtered, isOpen, listRef, selectItem, value]
  );
  /**
   * Change value of the search box to a corresponding selected value's label
   */
  useEffect(() => {
    if (value) {
      const [sectionName] = value.split('/');

      const section = options.find(({ label }) => label === _.startCase(sectionName));
      const selectedItem = section && section.items.find(({ value: itemValue }) => itemValue === value);

      setSearchText((selectedItem && selectedItem.label) || '');
    }else {
      if(!isOpen) setSearchText('');
    }
  }, [value, options, setSearchText]);
  /**
   * Other hooks
   */
  useEventListener('keydown', handleKeyDown, inputRef.current);
  useClickOutside(listRef, closeMenu);

  return (
    <FieldWrapper label={label} error={error} isError={isError} name={name}>
      <div className='relative z-10'>
        <TextInput
          name={name}
          placeholder={placeholder}
          onClick={() => {
            !isOpen && setShouldFilter(false);
            setIsOpen(!isOpen);
          }}
          value={searchText}
          onChange={({ target: { value: inputValue } }: React.ChangeEvent<HTMLInputElement>) => {
            setHovered(-1);
            setSearchText(inputValue);
            onSearchTextChange(inputValue);
            setShouldFilter(true);
            setIsOpen(true);
            onChange(''); // invalidate previously selected value
          }}
          ref={inputRef}
          Right={
            <SelectIcon
              className={clsx('mr-3 cursor-pointer', isOpen && 'transform rotate-180')}
              onClick={() => {
                !isOpen && setShouldFilter(false);
                setIsOpen(!isOpen);
              }}
            />
          }
          isError={isError}
          wrapperClassName='relative z-10'
          // hack to prevent annoying autofill on Chrome
          // which covers the list of options when the field is empty
          // https://stackoverflow.com/questions/15738259/disabling-chrome-autofill
          required={required}
          autoComplete='new-password'
          aria-haspopup
          aria-expanded={isOpen}
          aria-activedescendant={`option-for-${value}`}
          aria-owns={`listbox-${name}`}
          aria-required={required}
          aria-invalid={isError}
          aria-describedby={`error-for-${name}`}
          role='combobox'
        />
        <div
          ref={dropdownRef}
          className={clsx(
            'w-full pointer-events-none rounded-lg overflow-hidden shadow-menu absolute',
            isOpen || 'invisible'
          )}
          style={{ transform: 'translateY(-1rem)', paddingTop: '.875rem' }}
        >
          {isLoading ? (
            <div className="w-full p-2 flex justify-center"><Spinner /></div>
          ) :  (
            <ul
              ref={listRef}
              tabIndex={-1}
              className="mb-0 overflow-auto bg-white border-t-0 pointer-events-auto pt-4"
              style={{ maxHeight: `${maxVisible * itemHeight}px` }}
              role="listbox"
              id={`listbox-${name}`}
              aria-label={label}
            >
              {filtered.length > 0 ? (
                  filtered.map(({ label, items }, idx) => {
                  return (
                    <li key={`${label}-${idx}`}>
                      <span className="px-3 py-1 text-gray-40">{label}</span>

                      {items.map(({ value: optionValue, label }, index) => (
                        <div
                          id={`option-for-${optionValue}`}
                          key={optionValue}
                          onClick={() => selectItem({ value: optionValue, label })}
                          onKeyDown={() => selectItem({ value: optionValue, label })}
                          className={clsx(
                            'text-black-default cursor-pointer font-effra p-3 border-transparent',
                            'hover:bg-gold hover:border-primary-500 hover:shadow-dd-item-active',
                            'focus:outline-none focus-visible:ring',
                            optionValue === value
                              ? 'bg-gold border-t border-b border-primary-500 shadow-dd-item-active pl-4'
                              : 'border-l-4',
                            index === hovered && 'bg-gold border-primary-500 shadow-dd-item-active'
                          )}
                          role="option"
                          aria-selected={optionValue === value}
                          tabIndex={-1}
                        >
                          {label}
                        </div>
                      ))}
                    </li>
                  );
                })
              ) : (
                <li className="p-3 border-l-4 border-transparent text-black-default font-effra">No items</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </FieldWrapper>
  );
}
